/**
 * Bouton d'affiliation.
 *
 * La destination marchande n'apparaît jamais dans le HTML servi : pas de href,
 * pas d'ancre, rien qu'un <button> et une chaîne encodée dans un attribut data.
 * Le décodage se fait au clic, côté navigateur. Un robot qui lit la source ne
 * voit aucun lien sortant à suivre ni à attribuer.
 *
 * Encodage posé par Liquid : base64 de l'URL, puis chaîne inversée.
 * Ce n'est pas du chiffrement et ça ne prétend pas l'être : c'est ce qu'il faut
 * pour qu'aucune URL marchande ne traîne en clair dans la page.
 *
 * Chaque clic émet l'évènement `affiliation:clic` sur window. C'est le signal
 * de demande qu'on cherche à mesurer : à brancher sur l'outil d'analyse.
 */

const decode = (charge) => {
  try {
    const inverse = charge.split('').reverse().join('');
    return decodeURIComponent(escape(atob(inverse)));
  } catch {
    return null;
  }
};

class BoutonAffiliation extends HTMLElement {
  connectedCallback() {
    this.bouton = this.querySelector('[data-affiliation-declencheur]');
    if (!this.bouton || this.bouton.dataset.affiliationLie === 'oui') return;
    this.bouton.dataset.affiliationLie = 'oui';
    this.bouton.addEventListener('click', this.ouvre);
    // Le décodage à la première intention rend le clic instantané, sans
    // exposer l'URL avant que l'utilisateur ne manifeste l'intention d'y aller.
    this.bouton.addEventListener('pointerenter', this.prepare, { once: true });
    this.bouton.addEventListener('focus', this.prepare, { once: true });
  }

  disconnectedCallback() {
    this.bouton?.removeEventListener('click', this.ouvre);
  }

  prepare = () => {
    if (this.url === undefined) this.url = decode(this.dataset.charge || '');
  };

  ouvre = (evenement) => {
    evenement.preventDefault();
    this.prepare();

    if (!this.url || !/^https:\/\//i.test(this.url)) {
      // Aucune destination valide : on ne fabrique pas un clic dans le vide.
      this.dispatchEvent(new CustomEvent('affiliation:absente', { bubbles: true }));
      return;
    }

    const detail = {
      marchand: this.dataset.marchand || '',
      produit: this.dataset.produit || '',
      reference: this.dataset.reference || '',
      prix: this.dataset.prix || '',
      emplacement: this.dataset.emplacement || 'fiche-produit',
    };

    window.dispatchEvent(new CustomEvent('affiliation:clic', { detail }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'clic_affiliation', ...detail });
    }

    this.sors();
  };

  /**
   * Ouvre la destination dans un nouvel onglet, sans jamais toucher à la
   * fenêtre courante.
   *
   * On passe par un lien cliqué plutôt que par `window.open` : dès qu'on lui
   * passe `noopener` ou `noreferrer`, `window.open` renvoie `null` par
   * spécification, que l'onglet se soit ouvert ou non. Son retour ne dit donc
   * rien du succès et ne peut pas servir à détecter un bloqueur de fenêtres :
   * s'en servir revient à naviguer la page courante à chaque clic, en plus de
   * l'onglet qui s'ouvre. Un lien cliqué pendant un geste de l'utilisateur
   * ouvre l'onglet et laisse la page en place, avec les mêmes protections.
   *
   * L'URL n'entre dans le document qu'au clic, le temps de l'activation : le
   * HTML servi reste sans destination marchande.
   */
  sors() {
    const lien = document.createElement('a');
    lien.href = this.url;
    lien.target = '_blank';
    lien.rel = 'noopener noreferrer';
    lien.hidden = true;
    this.append(lien);
    lien.click();
    lien.remove();
  }
}

if (!customElements.get('bouton-affiliation')) {
  customElements.define('bouton-affiliation', BoutonAffiliation);
}
